import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { MessageCircle, BookOpen, Target, Headphones, ArrowRight, Flame, Star, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import DailyGlimmerPrompt from '@/components/DailyGlimmerPrompt';
import { DashboardSkeleton } from '@/components/Skeletons';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [recentConvs, setRecentConvs] = useState([]);
  const [voidStatus, setVoidStatus] = useState(null);
  const [voidDismissed, setVoidDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, promptRes, convsRes, voidRes] = await Promise.all([
          api.get('/stats'),
          api.get('/journal/prompt'),
          api.get('/conversations'),
          api.get('/echo-map/void-status'),
        ]);
        setStats(statsRes.data);
        setPrompt(promptRes.data);
        setRecentConvs(convsRes.data.slice(0, 3));
        setVoidStatus(voidRes.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Check session-level dismissal
    if (sessionStorage.getItem('void_banner_dismissed') === '1') {
      setVoidDismissed(true);
    }
  }, []);

  const dismissVoid = () => {
    sessionStorage.setItem('void_banner_dismissed', '1');
    setVoidDismissed(true);
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const milestoneProgress = stats?.milestones_total > 0
    ? Math.round((stats.milestones_completed / stats.milestones_total) * 100)
    : 0;

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Header */}
      <div className="animate-fade-up">
        <p className="text-gold font-sans text-xs tracking-[0.3em] uppercase mb-3">Your Sacred Space</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-dark tracking-tight mystical-heading">
          Welcome, {firstName}
        </h1>
        <p className="text-text-muted font-sans text-base mt-3 max-w-xl">
          What version of yourself will you step into today?
        </p>
      </div>

      {/* Email Verification handled by full-screen gate in DashboardLayout */}

      {/* The Shift / Void Banner */}
      {voidStatus?.in_void && !voidDismissed && (
        <div
          className="relative rounded-2xl bg-dark text-bone p-6 sm:p-7 animate-fade-up overflow-hidden"
          data-testid="void-banner"
          style={{ borderLeft: '3px solid #C6B28F' }}
        >
          <div className="absolute inset-0 opacity-30 pointer-events-none">
            {Array.from({ length: 14 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-bone"
                style={{
                  left: `${(i * 41) % 100}%`,
                  top: `${(i * 29) % 100}%`,
                  width: 1.5, height: 1.5,
                  opacity: 0.4 + ((i * 3) % 4) / 10,
                }}
              />
            ))}
          </div>
          <button
            onClick={dismissVoid}
            data-testid="void-banner-dismiss"
            className="absolute top-4 right-4 text-bone/50 hover:text-bone z-10"
            style={{ transition: 'color 0.2s ease' }}
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
            <Star size={28} className="text-gold shrink-0" />
            <div className="flex-1">
              <p className="text-gold font-sans text-[10px] tracking-[0.35em] uppercase mb-2">It's been quiet</p>
              <p className="font-serif text-xl sm:text-2xl text-bone leading-snug">
                It's been a few days. Settle in and notice what's already true.
              </p>
              {voidStatus.total_glimmers > 0 && voidStatus.hours_since_last_glimmer && (
                <p className="font-sans text-sm text-bone/60 mt-2">
                  No signs logged for {Math.round(voidStatus.hours_since_last_glimmer)}h.
                </p>
              )}
            </div>
            <Button
              onClick={() => navigate('/echo-map')}
              data-testid="void-banner-cta"
              className="bg-gold hover:bg-gold/90 text-dark rounded-xl font-sans text-xs tracking-[0.2em] uppercase shrink-0"
            >
              Open your Signs <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Daily Glimmer Prompt — once-a-day nudge to log evidence */}
      <DailyGlimmerPrompt />

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">

        {/* Daily Prompt Card */}
        <div className="md:col-span-5 lg:col-span-7 mystical-card p-8 animate-fade-up delay-100 group" data-testid="daily-prompt-card">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} className="text-gold" />
            <span className="text-gold font-sans text-xs tracking-[0.25em] uppercase">Today's Reflection</span>
          </div>
          <p className="font-serif text-xl sm:text-2xl text-dark leading-relaxed italic mb-6">
            "{prompt?.prompt || 'Loading your daily reflection...'}"
          </p>
          <Button
            data-testid="start-journaling-button"
            onClick={() => navigate('/journal')}
            className="bg-dark hover:bg-dark/90 text-bone rounded-xl font-sans text-sm tracking-wider btn-hover"
          >
            Begin Journaling <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        {/* Quick Actions */}
        <div className="md:col-span-3 lg:col-span-5 space-y-4 animate-fade-up delay-200">
          <button
            onClick={() => navigate('/chat')}
            data-testid="start-coaching-button"
            className="w-full glass-card-solid p-6 text-left hover-glow group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle size={16} className="text-gold" />
                  <span className="font-sans text-xs tracking-[0.25em] uppercase text-gold">AI Coach</span>
                </div>
                <p className="font-serif text-lg text-dark">Start a Session</p>
              </div>
              <ArrowRight size={20} className="text-text-muted group-hover:text-gold group-hover:translate-x-1" style={{ transition: 'transform 0.2s ease, color 0.2s ease' }} />
            </div>
          </button>

          <button
            onClick={() => navigate('/meditations')}
            data-testid="browse-meditations-button"
            className="w-full glass-card-solid p-6 text-left hover-glow group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Headphones size={16} className="text-moss" />
                  <span className="font-sans text-xs tracking-[0.25em] uppercase text-moss">Meditations</span>
                </div>
                <p className="font-serif text-lg text-dark">Browse Practices</p>
              </div>
              <ArrowRight size={20} className="text-text-muted group-hover:text-moss group-hover:translate-x-1" style={{ transition: 'transform 0.2s ease, color 0.2s ease' }} />
            </div>
          </button>

          <button
            onClick={() => navigate('/milestones')}
            data-testid="view-milestones-button"
            className="w-full glass-card-solid p-6 text-left hover-glow group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Target size={16} className="text-gold" />
                  <span className="font-sans text-xs tracking-[0.25em] uppercase text-gold">Milestones</span>
                </div>
                <p className="font-serif text-lg text-dark">Track Progress</p>
              </div>
              <ArrowRight size={20} className="text-text-muted group-hover:text-gold group-hover:translate-x-1" style={{ transition: 'transform 0.2s ease, color 0.2s ease' }} />
            </div>
          </button>
        </div>

        {/* Stats Row */}
        <div className="md:col-span-4 lg:col-span-4 glass-card p-6 animate-fade-up delay-300" data-testid="journal-streak-card">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={16} className="text-gold" />
            <span className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">Journal Streak</span>
          </div>
          <p className="font-serif text-5xl text-dark mb-1">{stats?.journal_streak || 0}</p>
          <p className="text-text-muted font-sans text-sm">consecutive days</p>
        </div>

        <div className="md:col-span-4 lg:col-span-4 glass-card p-6 animate-fade-up delay-400" data-testid="milestones-progress-card">
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} className="text-moss" />
            <span className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">Milestones</span>
          </div>
          <p className="font-serif text-3xl text-dark mb-2">
            {stats?.milestones_completed || 0}
            <span className="text-text-muted text-lg font-sans"> / {stats?.milestones_total || 0}</span>
          </p>
          <Progress value={milestoneProgress} className="h-1.5 bg-sand" />
        </div>

        <div className="md:col-span-4 lg:col-span-4 glass-card p-6 animate-fade-up delay-500" data-testid="sessions-card">
          <div className="flex items-center gap-2 mb-3">
            <MessageCircle size={16} className="text-gold" />
            <span className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">Coaching Sessions</span>
          </div>
          <p className="font-serif text-5xl text-dark mb-1">{stats?.conversation_count || 0}</p>
          <p className="text-text-muted font-sans text-sm">conversations with your coach</p>
        </div>

        {/* Recent Conversations */}
        {recentConvs.length > 0 && (
          <div className="md:col-span-8 lg:col-span-12 glass-card-solid p-8 animate-fade-up delay-300" data-testid="recent-conversations-card">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <MessageCircle size={16} className="text-moss" />
                <span className="text-text-muted font-sans text-xs tracking-[0.2em] uppercase">Recent Conversations</span>
              </div>
              <Button
                variant="ghost"
                onClick={() => navigate('/chat')}
                className="text-text-muted hover:text-dark font-sans text-sm"
                data-testid="view-all-conversations-button"
              >
                View All <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
            <div className="space-y-3">
              {recentConvs.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => navigate(`/chat/${conv.conversation_id}`)}
                  className="w-full text-left p-4 rounded-xl hover:bg-sand/60 group"
                  style={{ transition: 'background-color 0.2s ease' }}
                  data-testid={`conversation-${conv.conversation_id}`}
                >
                  <p className="font-serif text-lg text-dark group-hover:text-dark/80">{conv.title}</p>
                  <p className="text-text-muted font-sans text-xs mt-1">
                    {conv.message_count || 0} messages
                    {conv.updated_at && ` \u00B7 ${new Date(conv.updated_at).toLocaleDateString()}`}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
